import { LOCALES, messagesFor } from '@indusketch/i18n';
import type { ProjectSummary } from '@indusketch/shared';

export interface ProjectNode {
  readonly project: ProjectSummary;
  /** Variants (direct and indirect), flattened in creation order. */
  readonly variants: readonly ProjectSummary[];
}

export type SortKey = 'updated' | 'name';

/**
 * Groups variants under their root project so "Plan A / Plan B / Plan C" appear together.
 * A variant whose parent was deleted (or belongs to someone else) becomes a root itself.
 */
export function buildProjectTree(
  projects: readonly ProjectSummary[],
  query: string,
  sort: SortKey,
): ProjectNode[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const rootOf = (p: ProjectSummary): ProjectSummary => {
    let current = p;
    const seen = new Set<string>();
    while (current.parentProjectId && byId.has(current.parentProjectId) && !seen.has(current.id)) {
      seen.add(current.id);
      current = byId.get(current.parentProjectId) as ProjectSummary;
    }
    return current;
  };
  const groups = new Map<string, ProjectSummary[]>();
  for (const p of projects) {
    const root = rootOf(p);
    if (root.id === p.id) continue;
    groups.set(root.id, [...(groups.get(root.id) ?? []), p]);
  }
  const q = query.trim().toLocaleLowerCase('de');
  const matches = (p: ProjectSummary) =>
    !q ||
    p.name.toLocaleLowerCase('de').includes(q) ||
    p.description.toLocaleLowerCase('de').includes(q);
  const latest = (n: ProjectNode) =>
    Math.max(...[n.project, ...n.variants].map((p) => Date.parse(p.updatedAt)));

  const nodes = projects
    .filter((p) => rootOf(p).id === p.id)
    .map((p) => ({
      project: p,
      variants: (groups.get(p.id) ?? []).sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    }))
    .filter((n) => matches(n.project) || n.variants.some(matches));

  return nodes.sort((a, b) =>
    sort === 'name' ? a.project.name.localeCompare(b.project.name, 'de') : latest(b) - latest(a),
  );
}

/**
 * Suggests "Name – Variante B", "… C", … based on existing variant names. `word` is the
 * translated "variant"; a suffix in any language ("– Variant C") is replaced, not appended.
 */
export function nextVariantName(
  root: ProjectSummary,
  variants: readonly ProjectSummary[],
  word = 'Variante',
): string {
  const words = LOCALES.map((l) => messagesFor(l).projects.variant).join('|');
  const base = root.name.replace(new RegExp(`\\s+–\\s+(?:${words})\\s+[A-Z]$`), '');
  const used = new Set([root, ...variants].map((p) => p.name));
  for (let i = 1; i < 26; i++) {
    const name = `${base} – ${word} ${String.fromCharCode(65 + i)}`;
    if (!used.has(name)) return name;
  }
  return `${base} – ${word}`;
}
